import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { ExclamationTriangleIcon } from '@heroicons/react/24/outline';

export const Unauthorized: React.FC = () => {
  const { user, state } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [countdown, setCountdown] = useState(5);

  // Determine redirect path based on auth state
  const getRedirectPath = useCallback(() => {
    if (state === 'AUTHENTICATED' && user) {
      return '/analysis';
    }
    return '/login';
  }, [state, user]);

  // Try to determine what permission was missing based on the route
  const getPermissionMessage = () => {
    const path = location.state?.from || location.pathname;

    if (path.includes('/users')) {
      return 'You need administrative privileges to manage users.';
    }

    if (path.includes('/sites')) {
      if (!user?.isOnyxUser) {
        return 'This section is only available to ONYX Insight users.';
      }
      if (user?.role !== 'admin') {
        return 'Administrative privileges are required to manage sites.';
      }
    }

    if (path.includes('/admin')) {
      return 'Administrative privileges are required to access this section.';
    }

    return 'Your permissions have changed and you no longer have access to this page.';
  };

  // Handle countdown and redirect
  useEffect(() => {
    const timer = setInterval(() => {
      setCountdown((prev) => {
        if (prev <= 1) {
          clearInterval(timer);
          navigate(getRedirectPath(), { replace: true });
          return 0;
        }
        return prev - 1;
      });
    }, 1000);

    return () => clearInterval(timer);
  }, [navigate, getRedirectPath]);

  const handleRedirectNow = () => {
    navigate(getRedirectPath(), { replace: true });
  };

  // Calculate progress percentage
  const progressPercentage = ((5 - countdown) / 5) * 100;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50">
      <div className="bg-white p-8 rounded-lg shadow-xl max-w-md w-full">
        <div className="text-center">
          {/* Icon */}
          <div className="inline-flex items-center justify-center w-20 h-20 bg-red-50 rounded-full mb-4">
            <ExclamationTriangleIcon className="w-12 h-12 text-red-600" />
          </div>

          {/* Title */}
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Denied</h1>

          {/* Message */}
          <p className="text-gray-600 mb-4">{getPermissionMessage()}</p>

          {/* User info if available */}
          {user && (
            <div className="bg-gray-50 rounded-lg p-3 mb-6 text-sm text-gray-600">
              <div>
                <span className="font-medium">Current Role:</span> {user.role}
              </div>
              <div>
                <span className="font-medium">Company:</span>{' '}
                {user.company === 'onyx' ? 'ONYX' : 'External'}
              </div>
            </div>
          )}

          {/* Countdown */}
          <div className="mb-6">
            <p className="text-lg font-medium text-gray-700 mb-2">
              Redirecting in <span className="text-onyx-bright-blue font-bold">{countdown}</span>{' '}
              seconds...
            </p>

            {/* Progress bar */}
            <div className="w-full bg-gray-200 rounded-full h-2 overflow-hidden">
              <div
                className="bg-onyx-bright-blue h-full transition-all duration-1000 ease-linear"
                style={{ width: `${progressPercentage}%` }}
              />
            </div>
          </div>

          {/* Buttons */}
          <div className="space-y-3">
            <button
              onClick={handleRedirectNow}
              className="w-full px-4 py-2 bg-onyx-purple text-white font-medium rounded-lg hover:bg-onyx-dark-blue transition-colors"
            >
              {state === 'AUTHENTICATED' ? 'Go to Analytics App' : 'Go to Login Now'}
            </button>

            <p className="text-sm text-gray-500">
              If you believe you should have access to this resource, please contact your
              administrator.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
